import React from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import styled from 'styled-components';
import BallotHeader from '~/components/sections/HowToVote/Ballot/BallotHeader/BallotHeader';
import Preference from '~/components/sections/HowToVote/Ballot/Preference/PreferenceTas25';
import UpperHousePreference from '~/components/sections/HowToVote/Ballot/UpperHousePreference/UpperHousePreference';
import { getBallotColour } from '~/helpers/BallotHelper';
import { REGION, STATE } from '~/helpers/constants/ElectorateTypes';
import { Config } from '~/helpers/types/Config';
import { Electorate } from '~/helpers/types/Electorate';

type BallotProps = {
  /** Array of preferences for the ballot paper. */
  preferences: (any | any)[];
  /** Details of the election. */
  config: Config;
  /** Should the ballot be aligned to the right? */
  alignRight?: boolean;
  /** Electorate the ballot paper is for. */
  electorate: Electorate;
};

type InnerBallotProps = {
  electorateType: string;
};

/**
 * A ballot paper with all preferences.
 * Can be an upper ballot or lower ballot depending on the ElectorateType
 * of the preferences.
 */
const Ballot: React.SFC<BallotProps> = ({
  preferences, config, alignRight, electorate,
}) => {
  const sortedPreferences = preferences.sort((p1, p2) => {
    if (p1.position) {
      return p1.position - p2.position;
    }
    return p1.group.length - p2.group.length || p1.group.localeCompare(p2.group);
  });
  /* eslint-disable max-len */
  const greensPreference = preferences.find((p) => config.partyNameOnBallot.includes(p.party));
  const greensCandidate = {
    ...greensPreference,
    electorate,
  };
  const {
    ballot,
    showGroupLabelOnBallot,
    capitaliseUpperHouseParties,
    upperBallotLogoPosition,
    boldGreensCandidate,
    partyNameOnBallot,
    linkCandidateOnBallot,
  } = config;
  const ballotColour = getBallotColour(electorate, config);
  const InnerBallot = styled.div<InnerBallotProps>`
    border: 2px solid black;
    padding: 15px;
    max-width: ${(props) => (props.electorateType === STATE ? '1000px' : '350px')};
    margin-left: auto;
    margin-right: auto;
  `;
  let candidates;
  const padWidth = (12 - sortedPreferences.length * 2) / 2;
  if (electorate.type === REGION) {
    candidates = (
      <UpperHousePreference
        {...greensPreference}
        showGreensLogoOnBallot={ballot.showGreensLogoOnBallot}
        partyNameOnBallot={partyNameOnBallot}
      />
    );
  } else if (electorate.type === STATE) {
    candidates = sortedPreferences.map((pref) => (config.ballot.customState ? config.ballot.customState(pref) : (
      <Col key={pref.position} md={2} style={{ paddingLeft: '2px', paddingRight: '2px', paddingBottom: '5px' }}>
        <UpperHousePreference
          key={pref.position}
          showGreensLogoOnBallot={ballot.showGreensLogoOnBallot}
          showGroupLabelOnBallot={showGroupLabelOnBallot}
          capitaliseUpperHouseParties={capitaliseUpperHouseParties}
          upperBallotLogoPosition={upperBallotLogoPosition}
          partyNameOnBallot={partyNameOnBallot}
          {...pref}
        />
      </Col>
    )));
  } else {
    candidates = sortedPreferences.map((pref) => (config.ballot.customDistrict ? config.ballot.customDistrict(pref) : (
      <Preference
        key={pref.position}
        electorateName={electorate.name}
        showGreensLogoOnBallot={ballot.showGreensLogoOnBallot}
        boldGreensCandidate={boldGreensCandidate}
        partyNameOnBallot={partyNameOnBallot}
        linkCandidateOnBallot={linkCandidateOnBallot}
        {...pref}
      />
    )));
  }

  const AdelaideOpenBox = styled.div`
    text-align: center;
    width: 80%;
    margin-left: auto;
    margin-right: auto;
    border: 1px solid black;
    margin-bottom: 25px;
    padding: 8px;
  `;

  return (
    <Container style={{ padding: 0 }}>
      <Row>
        <InnerBallot electorateType={electorate.type} style={{ backgroundColor: ballotColour }}>
          <Row>
            <BallotHeader candidate={greensCandidate} config={config} />
          </Row>
          {electorate.type === 'State'
            ? (
              <>
                <Row>
                  {/* <Col md={padWidth} /> */}
                  {candidates}
                  {/* <Col md={padWidth} /> */}
                </Row>
                <Row>
                  <Col>
                    <div>If you choose you may continue numbering other groups in the order of your choice.</div>
                    <div style={{ paddingTop: 5 }}><small>Authorised by: A. Wheaton, The Greens, 7/291 Angas St, Adelaide SA 5000.</small></div>
                  </Col>
                </Row>
              </>
            ) : (
              // TODO remove adelaide specific
              electorate.name == "Adelaide" ? (
                <>
                  <Row>
                    <Preference key={1} partyNameOnBallot={["THE GREENS"]} electorateName={'Adelaide'} firstName={'Bronte'} lastName={'COLMER'} party='THE GREENS' preference={1} />
                  </Row>
                  <Row>
                    <AdelaideOpenBox>Then vote 2 to 6 for the other candidates according to your preference.</AdelaideOpenBox>
                    <small>Authorised by: A. Wheaton, The Greens, 7/291 Angas St, Adelaide SA 5000.</small>
                  </Row>
                </>

              ) : (<>
                <>
                  <Row>{candidates}</Row>
                  <small>Authorised by: A. Wheaton, The Greens, 7/291 Angas St, Adelaide SA 5000.</small>
                </>
              </>)
            )}
        </InnerBallot>
      </Row>
    </Container>
  );
};

export default Ballot;
